import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { cv } from '../content';
import { FadeUp, PageHeader } from '../components/Reveal';

const CV = () => {
    const { lang, t } = useLanguage();
    const data = cv[lang];

    return (
        <main data-testid="cv-page">
            <PageHeader
                kicker={`Robin Spencer — ${t.nav.cv}`}
                title={data.title}
                sub={data.intro}
            />

            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1200px] mx-auto">
                <div className="space-y-20 md:space-y-28">
                    {data.sections.map((section, sIdx) => (
                        <FadeUp key={section.heading} delay={sIdx * 0.05}>
                            <article
                                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start"
                                data-testid={`cv-section-${sIdx}`}
                            >
                                <header className="md:col-span-4">
                                    <span className="text-[10px] tracking-[0.35em] uppercase text-dusk">
                                        {String(sIdx + 1).padStart(2, '0')}
                                    </span>
                                    <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-tight tracking-tight text-paper">
                                        {section.heading}
                                    </h2>
                                </header>
                                <ul className="md:col-span-8 md:border-l md:border-white/10 md:pl-10 space-y-4" data-testid={`cv-list-${sIdx}`}>
                                    {section.items.map((item, iIdx) => {
                                        const dashIdx = item.indexOf(' — ');
                                        const year = dashIdx > 0 ? item.slice(0, dashIdx) : '';
                                        const body = dashIdx > 0 ? item.slice(dashIdx + 3) : item;
                                        return (
                                            <li
                                                key={`${sIdx}-${iIdx}`}
                                                className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 border-b border-white/5 pb-4"
                                                data-testid={`cv-item-${sIdx}-${iIdx}`}
                                            >
                                                {year && (
                                                    <span className="font-serif italic text-dusk text-sm tracking-wide sm:w-32 sm:shrink-0">
                                                        {year}
                                                    </span>
                                                )}
                                                <span className="text-paper text-sm md:text-base leading-relaxed">
                                                    {body}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </article>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1400px] mx-auto">
                <FadeUp>
                    <div className="relative overflow-hidden bg-surface border border-white/10 px-8 md:px-16 py-16 md:py-24" data-testid="cv-cta">
                        <div className="spotlight absolute inset-0" aria-hidden />
                        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-paper max-w-lg">
                                {t.about.title}
                            </h2>
                            <Link
                                to="/about"
                                className="group inline-flex w-fit items-center gap-3 rounded-full bg-paper px-7 py-3.5 text-sm font-medium text-ink transition-colors duration-300 hover:bg-dusk"
                                data-testid="cv-about-cta"
                            >
                                {t.nav.about}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </FadeUp>
            </section>
        </main>
    );
};

export default CV;
